import { analysisTrpcContract } from "@amtana/analysis-contract";
import type {
  Analysis,
  AnalysisList,
  Count,
  Mode,
  MoveInput,
  RecentAnalyses,
  RemoveInput,
  RenameInput,
  SearchTargetsInput,
  StartInput,
  Target,
  UpdateSettingsInput,
  UpdateWizardInput,
} from "@amtana/analysis-contract";
import {
  lentComponent,
  setSearchParam,
  useContractMutation,
  useContractQuery,
  useInvalidateContract,
} from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";

// Analyses for every browser module. A running analysis changes by itself, so
// reads refresh on an interval while asked to; every write refreshes all reads
// (credits, prospects and projects move with it).

export function useAnalyses(options: { live?: boolean } = {}): QueryResult<AnalysisList> {
  return useContractQuery(analysisTrpcContract, "list", {
    input: {},
    refetchInterval: options.live ? 2500 : false,
  });
}

/**
 * The current project's newest analyses (S39.01), for the home. `optional`: the home goes on
 * without them, showing the failure in their own column.
 */
export function useRecentAnalyses(
  limit: number,
  options: { live?: boolean } = {},
): QueryResult<RecentAnalyses> {
  return useContractQuery(analysisTrpcContract, "recent", {
    input: { limit },
    refetchInterval: options.live ? 5000 : false,
    optional: true,
  });
}

export function useAnalysis(
  analysisId: string | undefined,
  options: { live?: boolean } = {},
): QueryResult<Analysis> {
  return useContractQuery(analysisTrpcContract, "get", {
    input: { analysisId: analysisId ?? "" },
    refetchInterval: options.live ? 2000 : false,
    enabled: analysisId !== undefined,
  });
}

export function useSearchTargets(input: SearchTargetsInput | undefined): QueryResult<{ targets: Target[] }> {
  return useContractQuery(analysisTrpcContract, "searchTargets", {
    input: input ?? { mode: "account-followers", query: "-" },
    enabled: input !== undefined,
    // A failed search is the wizard's to show inline; the screen goes on.
    optional: true,
  });
}

function useWrite<
  TName extends
    | "create"
    | "updateWizard"
    | "start"
    | "pause"
    | "rename"
    | "updateSettings"
    | "remove"
    | "moveToProject",
  TInput,
  TOutput,
>(name: TName): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(analysisTrpcContract, name);
  const invalidate = useInvalidateContract();
  // The contract types `name`'s input and output; the hook restates them for consumers.
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  return { ...typed, run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate()) };
}

export const useCreateAnalysis = (): MutationResult<Analysis, Record<string, never>> => useWrite("create");
export const useUpdateWizard = (): MutationResult<Analysis, UpdateWizardInput> => useWrite("updateWizard");
export const useStartAnalysis = (): MutationResult<Analysis, StartInput> => useWrite("start");
export const usePauseAnalysis = (): MutationResult<Analysis, { analysisId: string }> => useWrite("pause");
export const useRenameAnalysis = (): MutationResult<Analysis, RenameInput> => useWrite("rename");
export const useUpdateAnalysisSettings = (): MutationResult<Analysis, UpdateSettingsInput> =>
  useWrite("updateSettings");
export const useRemoveAnalyses = (): MutationResult<Count, RemoveInput> => useWrite("remove");
export const useMoveAnalyses = (): MutationResult<Count, MoveInput> => useWrite("moveToProject");

/**
 * The "New analysis" drawer, mounted once by the app shell: it opens over any page when the URL
 * carries the parameter below, so a button, a chip or a command anywhere starts an analysis
 * without leaving the page.
 */
export const NewAnalysisHost: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "analysis",
  "NewAnalysisHost",
);

/** The home's "Latest analyses" column (S39.01): the newest five, each opening its analysis. */
export const LatestAnalyses: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "analysis",
  "LatestAnalyses",
);

/** The URL parameter the host reads; only analysis names it. */
export const NEW_ANALYSIS_PARAM = "new-analysis";

/** What the parameter says when nothing is chosen in advance. */
const ANY_SOURCE = "open";

/** Opens the "New analysis" drawer over the current page, with a source already chosen if given. */
export function openNewAnalysis(source?: Mode): void {
  setSearchParam(NEW_ANALYSIS_PARAM, source ?? ANY_SOURCE);
}

/** The drawer, as a link: `path` opened with "New analysis" over it. */
export function newAnalysisHref(path: string, source?: Mode): string {
  return `${path}?${NEW_ANALYSIS_PARAM}=${source ?? ANY_SOURCE}`;
}
